/**
 * InvoiceForm Component - T099
 *
 * Form for creating/editing invoices with items
 */
import { useEffect, useId, useState, type FormEvent } from 'react';
import { GlassPanel, Input, Select, Button, FormSaveBar } from '../ui';
import { CustomerSelect } from './CustomerSelect';
import { InvoiceItems } from './InvoiceItems';
import { InvoiceItemForm, type InvoiceItemDraft } from './InvoiceItemForm';
import { InvoiceTotals, calculateInvoiceTotals } from './InvoiceTotals';
import type { Invoice, InvoiceItem } from '../../hooks/useInvoices';
import type { Customer } from '../../hooks/useCustomers';
import { calculateLineTotal, type CreateInvoiceInput } from '@armard/shared';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

interface LocalInvoiceItem extends Omit<
  InvoiceItem,
  'id' | 'invoiceId' | 'createdAt' | 'updatedAt'
> {
  tempId: string;
}

interface InvoiceFormProps {
  invoice?: Invoice;
  initialCustomer?: Customer | null;
  onSubmit: (data: CreateInvoiceInput, items: InvoiceItemDraft[]) => void;
  onSubmitAndSend?: (data: CreateInvoiceInput, items: InvoiceItemDraft[]) => void;
  isLoading?: boolean;
  isSending?: boolean;
  onCancel?: () => void;
}

const vatRateOptions = [
  { value: '19', label: '19% (Standard)' },
  { value: '7', label: '7% (Ermäßigt)' },
  { value: '0', label: '0% (Steuerfrei)' },
];

function formatDateForInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function InvoiceForm({
  invoice,
  initialCustomer,
  onSubmit,
  onSubmitAndSend,
  isLoading,
  isSending,
  onCancel,
}: InvoiceFormProps) {
  const formId = useId();
  const today = new Date();

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    initialCustomer ??
      (invoice?.customer
        ? {
            id: invoice.customer.id,
            customerNumber: invoice.customer.customerNumber,
            companyName: invoice.customer.companyName,
            email: invoice.customer.email,
            contactFirstName: null,
            contactLastName: null,
            phone: null,
            defaultPaymentDays: 14,
            notes: null,
            isArchived: false,
            createdAt: '',
            updatedAt: '',
            version: 1,
          }
        : null)
  );

  const [formData, setFormData] = useState({
    customerId: invoice?.customerId ?? initialCustomer?.id ?? '',
    invoiceDate: formatDateForInput(invoice?.invoiceDate ?? today),
    dueDate: formatDateForInput(
      invoice?.dueDate ?? addDays(today, initialCustomer?.defaultPaymentDays ?? 14)
    ),
    vatRate: invoice?.vatRate ? parseFloat(invoice.vatRate) : 19,
    overallDiscountPercent: invoice?.overallDiscountPercent
      ? parseFloat(invoice.overallDiscountPercent)
      : 0,
    notes: invoice?.notes ?? '',
    customerReference: invoice?.customerReference ?? '',
    internalNotes: invoice?.internalNotes ?? '',
    showInternalNotesOnPdf: invoice?.showInternalNotesOnPdf ?? false,
  });

  const [items, setItems] = useState<LocalInvoiceItem[]>(
    invoice?.items.map((item) => ({
      ...item,
      tempId: item.id,
    })) ?? []
  );

  const [editingItem, setEditingItem] = useState<LocalInvoiceItem | null>(null);
  const [showItemForm, setShowItemForm] = useState(false);
  useUnsavedChanges({ formData, items, showItemForm, editingItem });

  useEffect(() => {
    if (!invoice && initialCustomer && !formData.customerId) {
      setSelectedCustomer(initialCustomer);
      setFormData((current) => ({
        ...current,
        customerId: initialCustomer.id,
        dueDate: formatDateForInput(
          addDays(new Date(current.invoiceDate), initialCustomer.defaultPaymentDays ?? 14)
        ),
      }));
    }
  }, [formData.customerId, initialCustomer, invoice]);

  const handleCustomerChange = (customerId: string, customer: Customer | null) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customerId,
      dueDate: customer
        ? formatDateForInput(
            addDays(new Date(formData.invoiceDate), customer.defaultPaymentDays ?? 14)
          )
        : formData.dueDate,
    });
  };

  const handleAddItem = (data: InvoiceItemDraft) => {
    const lineTotal = calculateLineTotal(
      data.quantity,
      data.unitPrice,
      data.discountType ?? undefined,
      data.discountValue ?? undefined
    );

    const newItem: LocalInvoiceItem = {
      tempId: `temp-${crypto.randomUUID()}`,
      position: items.length + 1,
      description: data.description,
      quantity: String(data.quantity),
      unitType: data.unitType || 'pieces',
      unitPrice: String(data.unitPrice),
      discountType: data.discountType || null,
      discountValue: data.discountValue == null ? null : String(data.discountValue),
      lineTotal: String(lineTotal),
    };

    setItems([...items, newItem]);
    setShowItemForm(false);
  };

  const handleUpdateItem = (data: InvoiceItemDraft) => {
    if (!editingItem) return;

    const lineTotal = calculateLineTotal(
      data.quantity,
      data.unitPrice,
      data.discountType ?? undefined,
      data.discountValue ?? undefined
    );

    setItems(
      items.map((item) =>
        item.tempId === editingItem.tempId
          ? {
              ...item,
              description: data.description,
              quantity: String(data.quantity),
              unitType: data.unitType || 'pieces',
              unitPrice: String(data.unitPrice),
              discountType: data.discountType || null,
              discountValue: data.discountValue == null ? null : String(data.discountValue),
              lineTotal: String(lineTotal),
            }
          : item
      )
    );
    setEditingItem(null);
  };

  const handleDeleteItem = (tempId: string) => {
    setItems(
      items
        .filter((item) => item.tempId !== tempId)
        .map((item, index) => ({
          ...item,
          position: index + 1,
        }))
    );
  };

  const totals = calculateInvoiceTotals(items, formData.vatRate, formData.overallDiscountPercent);

  const buildSubmitData = (): {
    invoiceData: CreateInvoiceInput;
    itemsData: InvoiceItemDraft[];
  } | null => {
    if (!formData.customerId || items.length === 0) return null;

    const invoiceData: CreateInvoiceInput = {
      customerId: formData.customerId,
      invoiceDate: formData.invoiceDate,
      dueDate: formData.dueDate,
      vatRate: formData.vatRate,
      overallDiscountPercent: formData.overallDiscountPercent,
      notes: formData.notes || undefined,
      customerReference: formData.customerReference || undefined,
      internalNotes: formData.internalNotes || undefined,
      showInternalNotesOnPdf: formData.showInternalNotesOnPdf,
    };

    // #17: cleared discounts stay EXPLICIT null all the way to the API's
    // update path ("clear"), instead of degrading to undefined ("keep").
    const itemsData: InvoiceItemDraft[] = items.map((item) => ({
      ...(item.tempId.startsWith('temp-') ? {} : { itemId: item.tempId }),
      description: item.description,
      quantity: parseFloat(item.quantity),
      unitType: item.unitType,
      unitPrice: parseFloat(item.unitPrice),
      discountType: item.discountType ?? null,
      discountValue: item.discountValue == null ? null : parseFloat(item.discountValue),
    }));

    return { invoiceData, itemsData };
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isLoading || isSending) return;
    const data = buildSubmitData();
    if (data) onSubmit(data.invoiceData, data.itemsData);
  };

  const handleSubmitAndSend = () => {
    if (isLoading || isSending) return;
    const data = buildSubmitData();
    if (data && onSubmitAndSend) onSubmitAndSend(data.invoiceData, data.itemsData);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <fieldset
          disabled={isLoading || isSending}
          aria-busy={isLoading || isSending}
          className="contents"
        >
          {/* Customer and Dates */}
          <GlassPanel className="mb-4">
            <h3 className="text-lg font-medium text-white mb-4">Rechnungsdaten</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <CustomerSelect
                  value={formData.customerId}
                  onChange={handleCustomerChange}
                  selectedCustomer={selectedCustomer}
                  error={!formData.customerId ? undefined : undefined}
                  disabled={!!invoice}
                />
              </div>

              <Input
                label="Rechnungsdatum"
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
              />

              <Input
                label="Fälligkeitsdatum"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />

              <Select
                label="MwSt.-Satz"
                options={vatRateOptions}
                value={String(formData.vatRate)}
                onChange={(e) => setFormData({ ...formData, vatRate: parseInt(e.target.value) })}
              />

              <Input
                label="Gesamtrabatt (%)"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={formData.overallDiscountPercent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    overallDiscountPercent: parseFloat(e.target.value) || 0,
                  })
                }
              />

              <Input
                label="Kunden-Referenz"
                value={formData.customerReference}
                onChange={(e) => setFormData({ ...formData, customerReference: e.target.value })}
                placeholder="Bestellnummer, Projektnummer, etc."
              />
            </div>
          </GlassPanel>

          {/* Invoice Items */}
          <GlassPanel className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Positionen</h3>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowItemForm(true)}
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Position
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-8 text-white/40">
                <p>Keine Positionen</p>
                <p className="text-sm mt-1">Fügen Sie Positionen zur Rechnung hinzu</p>
              </div>
            ) : (
              <InvoiceItems
                items={items}
                onEdit={(index) => setEditingItem(items[index])}
                onDelete={(index) => handleDeleteItem(items[index].tempId)}
              />
            )}

            {/* Totals */}
            {items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex justify-end">
                  <InvoiceTotals
                    overallDiscountPercent={formData.overallDiscountPercent}
                    items={items}
                    netTotal={totals.netTotal}
                    vatRate={formData.vatRate}
                    vatAmount={totals.vatAmount}
                    grossTotal={totals.grossTotal}
                    className="w-80 max-w-full"
                  />
                </div>
              </div>
            )}
          </GlassPanel>

          {/* Invoice text (appears on PDF) */}
          <GlassPanel className="mb-4">
            <label
              htmlFor={`${formId}-notes`}
              className="block text-lg font-medium text-white mb-4"
            >
              Rechnungstext
            </label>
            <textarea
              id={`${formId}-notes`}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-colors resize-none"
              placeholder="Einleitungstext (erscheint auf der Rechnung)..."
            />
          </GlassPanel>

          {/* Internal Notes */}
          <GlassPanel className="mb-4">
            <label
              htmlFor={`${formId}-internal-notes`}
              className="block text-lg font-medium text-white mb-4"
            >
              Interne Notizen
            </label>
            <textarea
              id={`${formId}-internal-notes`}
              value={formData.internalNotes}
              onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-colors resize-none"
              placeholder="Interne Notizen (nur für interne Zwecke)..."
            />
            <label className="flex items-center gap-2 mt-3 text-sm text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.showInternalNotesOnPdf}
                onChange={(e) =>
                  setFormData({ ...formData, showInternalNotesOnPdf: e.target.checked })
                }
                className="rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/30"
              />
              Auf PDF anzeigen
            </label>
          </GlassPanel>

          {/* 006b P2: die eine Save-Bar (PATTERNS.md §3) */}
          <FormSaveBar>
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel}>
                Abbrechen
              </Button>
            )}
            <Button
              type="submit"
              loading={isLoading}
              disabled={!formData.customerId || items.length === 0}
              variant={onSubmitAndSend ? 'secondary' : undefined}
            >
              {invoice ? 'Speichern' : 'Erstellen'}
            </Button>
            {onSubmitAndSend && !invoice && (
              <Button
                type="button"
                onClick={handleSubmitAndSend}
                loading={isSending}
                disabled={!formData.customerId || items.length === 0}
              >
                Erstellen und Senden
              </Button>
            )}
          </FormSaveBar>
        </fieldset>
      </form>

      {/* Item Form Modal */}
      {showItemForm && (
        <InvoiceItemForm onSubmit={handleAddItem} onCancel={() => setShowItemForm(false)} />
      )}

      {editingItem && (
        <InvoiceItemForm
          item={editingItem as unknown as InvoiceItem}
          onSubmit={handleUpdateItem}
          onCancel={() => setEditingItem(null)}
        />
      )}
    </>
  );
}
